import axios from 'axios'

type ApiErrorBody = {
  message?: unknown
  error?: unknown
  code?: unknown
  status?: unknown
  nuevoId?: unknown
  orderId?: unknown
  details?: unknown
}

export type FlexyStockInsufficientLine = {
  sku: string
  need: number
  disponible: number
}

export type FlexyStockInsufficientCreatedOrder = {
  orderId: number
  message: string
  status: string
  code: string
  insufficient: FlexyStockInsufficientLine[]
}

export class FlexyStockInsufficientOrderError extends Error {
  readonly created: FlexyStockInsufficientCreatedOrder

  constructor(created: FlexyStockInsufficientCreatedOrder) {
    super(created.message)
    this.name = 'FlexyStockInsufficientOrderError'
    this.created = created
  }
}

function parseInsufficientLines(details: unknown): FlexyStockInsufficientLine[] {
  if (!details || typeof details !== 'object') return []
  const raw = (details as { insufficient?: unknown }).insufficient
  if (!Array.isArray(raw)) return []
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null
      const item = row as Record<string, unknown>
      const sku = String(item.sku ?? '').trim()
      if (!sku) return null
      return {
        sku,
        need: Number(item.need) || 0,
        disponible: Number(item.disponible) || 0
      }
    })
    .filter((row): row is FlexyStockInsufficientLine => row != null)
}

function readApiErrorText(value: unknown): string {
  if (typeof value !== 'string') return ''
  return value.trim()
}

/** Mensaje API de stock Flexy con sufijo «EN FLEXY». */
export function formatFlexyStockInsufficientMessage(message?: string): string {
  const base =
    readApiErrorText(message) || 'Stock insuficiente para uno o más productos'
  if (/\ben\s+flexy\b/i.test(base)) return base
  return `${base} EN FLEXY`
}

/** Lista legible de SKUs sin saldo desde details.insufficient. */
export function formatFlexyInsufficientSkuSummary(
  insufficient: FlexyStockInsufficientLine[]
): string {
  if (!insufficient.length) return ''
  return insufficient
    .map((row) => `${row.sku} (necesita ${row.need}, disponible ${row.disponible})`)
    .join('; ')
}

/** Mensaje Flexy + detalle de SKUs sin saldo. */
export function formatFlexyStockInsufficientMessageWithSkus(
  message?: string,
  insufficient: FlexyStockInsufficientLine[] = []
): string {
  const base = formatFlexyStockInsufficientMessage(message)
  const skuSummary = formatFlexyInsufficientSkuSummary(insufficient)
  if (!skuSummary) return base
  return `${base}. Sin saldo: ${skuSummary}`
}

export function buildFlexyStockInsufficientToastMessage(
  created: FlexyStockInsufficientCreatedOrder,
  context: 'create' | 'update' = 'create'
): string {
  const action =
    context === 'update'
      ? 'El pedido se guardó en Flexy pero quedó sin stock suficiente'
      : 'El pedido se creó en Flexy pero quedó sin stock suficiente'
  const withSkus = formatFlexyStockInsufficientMessageWithSkus(
    created.message,
    created.insufficient
  )
  return `${action}. ${withSkus} Pedido #${created.orderId}.`
}

/**
 * POST /orders o PUT /list/:id pueden responder 422 con pedido creado/actualizado (sin_stock_flexy).
 */
export function parseFlexyStockInsufficientCreatedOrder(
  error: unknown
): FlexyStockInsufficientCreatedOrder | null {
  if (!axios.isAxiosError(error)) return null
  const data = error.response?.data as ApiErrorBody | string | undefined
  if (!data || typeof data !== 'object') return null

  const code = String(data.code ?? '')
  const status = String(data.status ?? '')
  const orderId = Number(data.nuevoId ?? data.orderId)
  if (!Number.isFinite(orderId) || orderId <= 0) return null

  const isCreatedDespiteStockError =
    code === 'FLEXY_STOCK_INSUFFICIENT' || status === 'sin_stock_flexy'
  if (!isCreatedDespiteStockError) return null

  return {
    orderId,
    message: formatFlexyStockInsufficientMessage(readApiErrorText(data.message)),
    status,
    code,
    insufficient: parseInsufficientLines(data.details)
  }
}

export function getFlexyStockInsufficientFromError(
  error: unknown
): FlexyStockInsufficientCreatedOrder | null {
  if (error instanceof FlexyStockInsufficientOrderError) {
    return error.created
  }
  return parseFlexyStockInsufficientCreatedOrder(error)
}

/** True si el API respondió FLEXY_STOCK_INSUFFICIENT (con o sin orderId). */
export function isFlexyStockInsufficientError(error: unknown): boolean {
  if (error instanceof FlexyStockInsufficientOrderError) return true
  if (!axios.isAxiosError(error)) return false
  const data = error.response?.data as ApiErrorBody | string | undefined
  if (!data || typeof data !== 'object') return false
  const code = String(data.code ?? '')
  const status = String(data.status ?? '')
  return code === 'FLEXY_STOCK_INSUFFICIENT' || status === 'sin_stock_flexy'
}

/**
 * Extrae mensaje legible de errores Axios ({ error, message } del API).
 */
export function extractAxiosApiErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiErrorBody | string | undefined

    if (typeof data === 'string' && data.trim()) {
      return data.trim()
    }

    if (data && typeof data === 'object') {
      const code = String(data.code ?? '')
      const message = readApiErrorText(data.message)
      const err = readApiErrorText(data.error)

      if (code === 'FLEXY_STOCK_INSUFFICIENT' || String(data.status ?? '') === 'sin_stock_flexy') {
        return formatFlexyStockInsufficientMessageWithSkus(
          message,
          parseInsufficientLines(data.details)
        )
      }

      if (message && err && message !== err) {
        return `${err}: ${message}`
      }
      if (message) return message
      if (err) return err
    }

    if (error.message?.trim()) {
      return error.message.trim()
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim()
  }

  return fallback
}
